import React from 'react';
import { Plugin, Template, TemplatePlaceholder } from '@devexpress/dx-react-core';
import CreateScheduleGroupIcon from '@mui/icons-material/PendingActions';
import { Box, Button, Tooltip } from '@mui/material';

import { InlineNotification } from '@paragon/notification-tools';
import { getRanges } from '@paragon/utilities';

import { showCreateOrAddToScheduleGroupDialog } from '../../../actions/dialogActions';
import {
  getBacklogGridCheckedBundleGuids,
  getBacklogGridRows,
  isGroupedProductionComponentInstanceRow,
} from '../../../selectors/backlogGrid';
import { getProductionComponentInstancesByBundleGuid } from '../../../selectors/getProductionComponentInstancesByBundleGuid';
import { RootState } from '../../../store';
import { collapseRows } from '../../../store/backlogGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CollapseAllButton } from '../CollapseAllButton';
import { DownloadProductionPackageButton } from '../DownloadProductionPackageButton';

export function BacklogGridToolbarPlugin() {
  const dispatch = useAppDispatch();
  const bundles = useAppSelector((state: RootState) => state.bundles);
  const productionComponentInstancesByBundleGuid = useAppSelector(getProductionComponentInstancesByBundleGuid);
  const checkedBundleGuids = useAppSelector(getBacklogGridCheckedBundleGuids);
  const emptyCheckedBundleGuids = checkedBundleGuids.filter(
    (bundleGuid) => productionComponentInstancesByBundleGuid[bundleGuid].length === 0,
  );
  const areEmptyCheckedBundles = emptyCheckedBundleGuids.length > 0;
  const backlogGridRows = useAppSelector(getBacklogGridRows);
  const allExpandableOrCollapsableRowIds = backlogGridRows
    .filter((row) => !isGroupedProductionComponentInstanceRow(row.rowId))
    .map((row) => row.rowId);

  return (
    <Plugin name="BacklogGridToolbar" dependencies={[{ name: 'Toolbar' }]}>
      <Template name="toolbarContent">
        <Box sx={{ display: 'flex', flexDirection: 'row', verticalAlign: 'middle' }}>
          <Box sx={{ p: 1 }}>
            <Tooltip
              disableInteractive
              title={
                checkedBundleGuids.length === 0
                  ? 'Select bundles to create/add to schedule group'
                  : 'Create/Add to schedule group'
              }
            >
              <span>
                <Button
                  onClick={() => void dispatch(showCreateOrAddToScheduleGroupDialog())}
                  variant="outlined"
                  startIcon={<CreateScheduleGroupIcon />}
                  disabled={checkedBundleGuids.length === 0 || areEmptyCheckedBundles}
                >
                  Group for production
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ p: 1 }}>
            <DownloadProductionPackageButton
              bundleGuids={checkedBundleGuids}
              availableOutputTypes={['ProductionAndShippingList', 'ComponentReport', 'LumberPickList', 'PlatePickList']}
              productionArtifactsContext="ProductionGroup"
            />
          </Box>

          {areEmptyCheckedBundles ? (
            <Box sx={{ p: 1 }}>
              <InlineNotification type="error">
                Cannot schedule empty bundles:{' '}
                {getRanges(emptyCheckedBundleGuids.map((bundleGuid) => bundles[bundleGuid].id)).join(', ')}
              </InlineNotification>
            </Box>
          ) : null}
        </Box>
        <TemplatePlaceholder />
        <CollapseAllButton
          collapseAll={(rowIds) => dispatch(collapseRows(rowIds))}
          allCollapsableRowIds={allExpandableOrCollapsableRowIds}
        />
      </Template>
    </Plugin>
  );
}
